import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { api, API_BASE, setAccountDeactivatedHandler } from "../utils/api";
import { setSessionHint } from "../utils/sessionHint";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [pendingPhone, setPendingPhone] = useState(null);

  // Refetch /auth/me. Used on mount and after impersonation start/stop
  // to pick up the target user without a hard page reload.
  const reload = useCallback(async () => {
    try {
      const data = await api.get("/auth/me");
      setUser(data.user || null);
      setImpersonation(data.impersonation || null);
      return { ok: true, user: data.user, impersonation: data.impersonation };
    } catch {
      setUser(null);
      setImpersonation(null);
      return { ok: false };
    }
  }, []);

  // On mount: try to hydrate user from httpOnly cookie via /me endpoint.
  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  // Mirror user presence to a localStorage hint so Landing.js and
  // LandingNav can render the right thing on first paint of the next
  // visit without waiting for /auth/me. Gate on !loading so the initial
  // (user=null, loading=true) state doesn't clobber a valid prior hint
  // before /auth/me resolves.
  useEffect(() => {
    if (!loading) setSessionHint(!!user);
  }, [user, loading]);

  // Global fallback for mid-session deactivation. When ANY authenticated
  // API call returns 403 with error=account_deactivated, the api layer
  // invokes this handler — we clear the in-memory user state and bounce
  // the browser to /signin with a friendly message.
  useEffect(() => {
    setAccountDeactivatedHandler(() => {
      setUser(null);
      setImpersonation(null);
      setPendingEmail(null);
      setPendingPhone(null);

      // Guard against a redirect loop: if we're ALREADY on the signin
      // page with the deactivated error, do nothing. (The backend
      // clears paysafi_token on the 403, so this should never happen
      // in practice, but belt-and-braces avoids a hard-to-debug loop
      // if a stale cookie somehow persists.)
      if (
        window.location.pathname === "/signin" &&
        window.location.search.includes("error=account-deactivated")
      ) {
        return;
      }
      window.location.href = "/signin?error=account-deactivated";
    });
    return () => setAccountDeactivatedHandler(null);
  }, []);

  // Stop an active admin-impersonation session. Clears the impersonation
  // cookie server-side, then refetches /auth/me (which will now return
  // 401 since the admin was not a user session holder).
  const stopImpersonating = useCallback(async () => {
    try {
      await api.post("/admin/stop-impersonate");
    } catch {
      // swallow — still clear local state
    }
    setUser(null);
    setImpersonation(null);
  }, []);

  // ── Email Signup ─────────────────────────────────────────────────────

  const signUpWithEmail = useCallback(async ({ fullName, email, password }) => {
    const data = await api.post("/auth/signup", { fullName, email, password });
    // Resume case: user already finished email verification on a previous
    // session but never added a phone number. Backend has already set a
    // fresh JWT cookie; hydrate the user directly and skip /verify.
    if (data.resumed && data.user) {
      setUser(data.user);
      setPendingEmail(null);
      return data;
    }
    setPendingEmail(email);
    return data;
  }, []);

  // ── Email Signin ─────────────────────────────────────────────────────

  const signInWithEmail = useCallback(async ({ email, password }) => {
    const data = await api.post("/auth/signin", { email, password });
    // Only stash pendingEmail for the normal user flow. When the backend
    // forks into the admin path (route === "admin"), SignIn.js will
    // stash the email on AdminAuthContext instead.
    if (data.route !== "admin") {
      setPendingEmail(email);
    }
    return data;
  }, []);

  // ── Verify email code ────────────────────────────────────────────────

  const verifyCode = useCallback(
    async (code) => {
      if (!pendingEmail) {
        return { ok: false, error: "No pending verification" };
      }
      try {
        const data = await api.post("/auth/verify-email", {
          email: pendingEmail,
          code,
        });
        setUser(data.user);
        setPendingEmail(null);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
    [pendingEmail]
  );

  // ── Resend code ──────────────────────────────────────────────────────

  const sendCode = useCallback(
    async (email) => {
      const target = email || pendingEmail;
      if (!target) return { ok: false, error: "No email to resend to" };
      try {
        await api.post("/auth/resend-code", { email: target });
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message || "Failed to resend code" };
      }
    },
    [pendingEmail]
  );

  // ── Google OAuth ─────────────────────────────────────────────────────

  const signInWithGoogle = useCallback(({ isSignup = false } = {}) => {
    const url = `${API_BASE}/api/auth/google?signup=${isSignup}`;
    window.location.href = url;
  }, []);

  // ── Sign out ─────────────────────────────────────────────────────────

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/signout");
    } catch {
      // Clear local state regardless
    }
    setUser(null);
    setPendingEmail(null);
    setPendingPhone(null);
  }, []);

  // ── Delete account ───────────────────────────────────────────────────

  const deleteAccount = useCallback(async () => {
    await api.post("/auth/delete-account");
    setUser(null);
    setPendingEmail(null);
    setPendingPhone(null);
  }, []);

  // ── Phone OTP via Africa's Talking SMS ───────────────────────────────

  const sendPhoneOtp = useCallback(async (phoneInfo) => {
    const target = phoneInfo || pendingPhone;
    if (!target?.e164) {
      return { ok: false, error: "Enter a valid phone number" };
    }
    try {
      await api.post("/auth/phone/send-code", {
        e164: target.e164,
        countryCode: target.countryCode,
        phone: target.phone,
        dial: target.dial,
      });
      setPendingPhone(target);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Failed to send code" };
    }
  }, [pendingPhone]);

  const verifyPhoneOtp = useCallback(
    async (code) => {
      if (!pendingPhone) {
        return { ok: false, error: "No pending verification" };
      }
      try {
        const data = await api.post("/auth/phone/verify-code", { code });
        if (data.user) {
          setUser(data.user);
        } else {
          setUser((prev) =>
            prev ? { ...prev, phone: pendingPhone, needsPhone: false } : prev
          );
        }
        setPendingPhone(null);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message || "Invalid code" };
      }
    },
    [pendingPhone]
  );

  const clearPendingPhone = useCallback(() => setPendingPhone(null), []);

  const value = {
    user,
    impersonation,
    loading,
    pendingEmail,
    pendingUser: pendingEmail ? { email: pendingEmail } : null,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    verifyCode,
    sendCode,
    signOut,
    deleteAccount,
    pendingPhone,
    sendPhoneOtp,
    verifyPhoneOtp,
    clearPendingPhone,
    reload,
    stopImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function maskEmail(email) {
  if (!email) return "";
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const visible = name.slice(0, 3);
  return `${visible}${"*".repeat(Math.max(0, name.length - 3))}@${domain}`;
}
